import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Flex,
  Button,
  HStack,
  IconButton,
  useDisclosure,
  VStack,
  Collapse,
  Image,
  Link,
} from "@chakra-ui/react";
import { HamburgerIcon, CloseIcon } from "@chakra-ui/icons";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import { useScroll } from "@/context/ScrollContext";
import SagaFiscalLogo from "../assets/images/img/saga_fiscal_logo.webp";
import PrimaryButton from "./PrimaryButton";

const Header: React.FC = () => {
  const { isOpen, onToggle } = useDisclosure();
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isHome = location.pathname === "/";

  const { scrollToRef, benefitsRef } = useScroll();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = useMemo(() => [
    { label: "Inicio",      path: "/",          action: () => navigate("/"),           isSection: false },
    { label: "Beneficios",  path: null,          action: () => isHome ? scrollToRef(benefitsRef) : navigate("/", { state: { scrollTo: "beneficios" } }), isSection: true },
    { label: "FAQs",        path: "/faqs",       action: () => navigate("/faqs"),       isSection: false },
    { label: "Contacto",    path: "/contactos",  action: () => navigate("/contactos"),  isSection: false },
  ], [isHome, navigate, scrollToRef, benefitsRef]);

  const isActive = (path: string | null) =>
    path !== null && location.pathname === path;

  return (
    <Box
      position="fixed"
      top={0}
      mt={scrolled ? 0 : 4}
      mx={scrolled ? 0 : 6}
      h={{ base: "64px", md: "80px" }}
      left={0}
      right={0}
      borderRadius={scrolled ? "0" : "full"}
      zIndex={100}
      transition="all 0.3s ease"
      bg="white"
      boxShadow={scrolled ? "sm" : "none"}
    >
      {/* CONTENEDOR PRINCIPAL */}
      <Flex
        align="center"
        justify="space-between"
        px={{ base: 4, md: 8 }}
        py={{ base: 2, md: 3 }}
        h="100%"
      >
        {/* IZQUIERDA */}
        <HStack spacing={{ base: 4, md: 8 }}>
          <Link as={RouterLink} to="/" display="inline-flex">
            <Image
              src={SagaFiscalLogo}
              alt="SAGA Fiscal - Inicio"
              htmlWidth={480}
              htmlHeight={110}
              w={{ base: "110px", md: "170px" }}
              h={{ base: "20px", md: "32px" }}
              objectFit="contain"
            />
          </Link>

          {/* LINKS DESKTOP */}
          <HStack spacing={3} display={{ base: "none", lg: "flex" }}>
            {navItems.map((item) => (
              <Button
                key={item.label}
                variant="ghost"
                fontWeight={isActive(item.path) ? 700 : 500}
                fontSize="16px"
                color={isActive(item.path) ? "blue.500" : "#2A2B2E"}
                borderBottom={isActive(item.path) ? "2px solid" : "2px solid transparent"}
                borderColor={isActive(item.path) ? "blue.500" : "transparent"}
                borderRadius={0}
                _hover={{ color: "blue.500" }}
                onClick={item.action}
              >
                {item.label}
              </Button>
            ))}
          </HStack>
        </HStack>

        {/* DERECHA */}
        <HStack spacing={3} display={{ base: "none", lg: "flex" }}>
          <Button
            bg="blue.500"
            h="44px"
            borderRadius="full"
            px={6}
            fontWeight={500}
            fontSize="14px"
            color="white"
            _hover={{ bg: "#0254d6" }}
            onClick={() => navigate("/contactos", { state: { asunto: "Solicitud de demo de landing saga fiscal" } })}
          >
            Agenda una demo
          </Button>
        </HStack>

        {/* MENÚ MÓVIL */}
        <IconButton
          display={{ base: "flex", lg: "none" }}
          aria-label="Abrir menú"
          icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
          onClick={onToggle}
          variant="ghost"
        />
      </Flex>

      {/* NAVEGACIÓN MÓVIL */}
      <Collapse in={isOpen} animateOpacity>
        <VStack
          bg="white"
          shadow="lg"
          spacing={2}
          py={4}
          px={4}
          display={{ lg: "none" }}
          borderRadius={25}
        >
          {navItems.map((item) => (
            <Button
              key={item.label}
              variant="ghost"
              w="100%"
              fontSize="14px"
              fontWeight={isActive(item.path) ? 700 : 500}
              color={isActive(item.path) ? "blue.500" : "#2A2B2E"}
              onClick={() => { item.action(); onToggle(); }}
            >
              {item.label}
            </Button>
          ))}

          <PrimaryButton
            w="100%"
            bg="#0367FD"
            h="44px"
            borderRadius="full"
            px={6}
            fontWeight={500}
            fontSize="14px"
            color="white"
            _hover={{ bg: "#0254d6" }}
            onClick={() => { navigate("/contactos", { state: { asunto: "Solicitud de demo de landing saga fiscal" } }); onToggle(); }}
          >
            Agenda una demo
          </PrimaryButton>
        </VStack>
      </Collapse>
    </Box>
  );
};

export default Header;
